import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import {
    UpcomingEventsInfoDocument,
    UpcomingEventsInfoQuery,
    UpcomingEventsInfoQueryVariables,
} from '@/api/events/query.generated';
import { ErrorIcon } from '@/assets/icons/error';
import { Box } from '@/components/lib/components';
import { useValidationSheet } from '@/feature/alerts/components/ValidationSheetProvider';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useModals } from '@/feature/alerts/hooks/use-modals';
import {
    ValidateEntryWithRulesDocument,
    ValidateEntryWithRulesQuery,
    ValidateEntryWithRulesQueryVariables,
} from '@/feature/betslip-pickem/api/query.generated';
import { useLineupUpdate } from '@/feature/betslip-pickem/components/LineupUpdateModalProvider';
import { useRestrictedCombo } from '@/feature/betslip-pickem/components/RestrictedCombinationModalProvider';
import { BetslipPick, PlayerWithTeam } from '@/feature/betslip-pickem/types';
import { getInvalidPicks } from '@/feature/betslip-pickem/utils/betslip-projection-changed';
import { mapPicksToApiData, mapValidationDataToGameModes } from '@/feature/betslip-pickem/utils/betslip-utils';
import { activeLimitsByTypeSelector, useUserLimits } from '@/feature/responsible-gaming/hooks/use-user-limits';
import { useAuthUserSettings } from '@/hooks/use-auth-user-settings';
import { useGetOrderedLeagues } from '@/hooks/use-fantasy-league-configs';
import { useGlobalState } from '@/hooks/use-global-state';
import { useWalletStore } from '@/hooks/use-wallet';
import { Currency, GameMode } from '@/types/api.generated';
import { getSubmitEntryErrorMsg, getWagerLimitErrorMessage } from '@/utils/errors/entry-errors';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';
import { logger } from '@/utils/logging';
import { CancelablePromise } from '@/utils/promises';
import { runStoreUpdate } from '@/utils/zustand';
import { OperationResult, useClient } from 'urql';

import { BetValidationData } from '../api/types';
import { useEdgeCombo } from '../components/EdgeComboModalProvider';
import { useInfoLineupUpdate } from '../components/InfoLineupUpdateModalProvider';
import { usePromoRemoved } from '../components/PromoRemovedModalProvider';
import { ChainProcessors } from '../processors';
import { PicksEdgeComboProcessor } from '../processors/edge-combos-processor';
import { PicksExceedsEdgeComboProcessor } from '../processors/exceeds-edge-combos-processor';
import { InvalidPicksProcessor } from '../processors/invalid-picks-processor';
import { PromotionsProcessor } from '../processors/promotions-processor';
import { PicksRestrictionsProcessor } from '../processors/restrictions-processor';
import { PicksValidateResultProcessor } from '../processors/validate-result-processor';
import { eventUtils } from '../utils/event-utils';
import { useBetslipNavigation } from './use-betslip-navigation';
import { useBetslipStore } from './use-betslip-store';
import { usePlayerPropsStore } from './use-player-props-store';

const LOG_TAG = '[Betslip]';

export type EntryAmountValidationData = {
    validEntry: boolean;
    errorMessage: string | undefined;
    maxAllowedEntryAmount: number;
};

let validatingPicksPromise:
    | CancelablePromise<OperationResult<ValidateEntryWithRulesQuery, ValidateEntryWithRulesQueryVariables>>
    | undefined;

//Latest added pick, that triggered the validatePicks call
//If the validatePicks call is canceled, the next validatePicks call can pick it up
//When validatePicks finished, this gets cleared
let lastPickToValidate: BetslipPick | undefined;

export const useBetslipActions = (_tag?: string) => {
    const navigation = useNavigation();
    const gqlClient = useClient();
    const { navigateToPickslip } = useBetslipNavigation();
    const { selectPick, removePicks, setValidationData, setValidating, setIsSyncingPicks, updateBetslipProjections } =
        useBetslipStore(state => state.actions);
    const updateStorePlayersProjections = usePlayerPropsStore(state => state.actions.updateStorePlayersProjections);
    const allEntryRules = useGlobalState(state => state.allEntryRules);
    const restrictedComboAlertContext = useRestrictedCombo();
    const { showValidationSheet, dismissValidationSheet } = useValidationSheet();
    const alertsContext = useAlerts();
    const { showInfoSheet } = alertsContext;
    const { dismissAll } = useModals();
    const { showLineupUpdateModal } = useLineupUpdate();

    const edgeComboDialogContext = useEdgeCombo();
    const { showPromoRemovedModal } = usePromoRemoved();
    const { showInfoLineupUpdateModal } = useInfoLineupUpdate();
    const { leagues } = useGetOrderedLeagues();
    const activeLeagues = useMemo(() => leagues.map(league => league.league), [leagues]);
    const { data: userSettings } = useAuthUserSettings();
    const acceptAllProjectionsChanges = userSettings && userSettings.accept_all_odds_changes;
    const { t } = useTranslation(['betslip_pickem', 'common', 'lineup_update_modal', 'error']);

    const betrBucks = useWalletStore(state => state.betrBucks);
    const realMoneyTotal = useWalletStore(state => state.realMoneyTotal);

    const { data: userLimits } = useUserLimits({
        select: activeLimitsByTypeSelector(['SINGLE_WAGER', 'WAGER']),
    });

    const picksInDisabledLeagues = useCallback(
        (picks: BetslipPick[]) => {
            return picks.filter(pick => !activeLeagues.includes(pick.player.league)).length > 0;
        },
        [activeLeagues]
    );

    const showBetslipValidatingDialog = useCallback(() => {
        showValidationSheet();
    }, [showValidationSheet]);

    const syncPicks = useCallback(
        async (opts: {
            expectError: boolean;
            promptIfOddsChanged?: boolean;
            infoModalDismissBehaviour?: 'goBack' | 'none';
        }) => {
            const { expectError, promptIfOddsChanged, infoModalDismissBehaviour } = opts;
            runStoreUpdate(() => {
                setIsSyncingPicks(true);
            });
            //always sync against latest picks from the store; there were edge cases where cached instances of
            //syncPicks callbacks were used, against older/outdated picks
            const currentPicks = useBetslipStore.getState().betslip ?? [];
            logger.info(LOG_TAG, 'Syncing picks, make sure projections up to date.', 'expect error->', expectError);
            const eventIds = currentPicks?.map(it => it.eventId) ?? [];
            const { data: eventsInfoData } = await gqlClient
                .query<UpcomingEventsInfoQuery, UpcomingEventsInfoQueryVariables>(
                    UpcomingEventsInfoDocument,
                    { ids: eventIds },
                    { requestPolicy: 'network-only' }
                )
                .toPromise();
            runStoreUpdate(() => {
                setIsSyncingPicks(false);
            });
            const events = eventsInfoData?.getUpcomingEventsByIdsV2 ?? [];
            runStoreUpdate(() => updateStorePlayersProjections(eventUtils.getAllPlayersAsMap(events)));
            const {
                invalidPicksByEvent,
                picksWithMissingEvents,
                picksWithChangedProjections,
                picksWithChangedMarketIds,
                picksWithChangedMarketStatus,
            } = getInvalidPicks(currentPicks, events);

            if (picksWithChangedMarketIds.length > 0) {
                const players = picksWithChangedMarketIds
                    .map(p => `${p.pick.player.firstName} ${p.pick.player.lastName}`)
                    .join(', ');
                logger.debug('SyncPicks - Market ID changed for some picks, updating them in the betslip', players);
                updateBetslipProjections([...picksWithChangedMarketIds]);
            }

            if (picksWithChangedMarketStatus.length > 0) {
                const players = picksWithChangedMarketStatus
                    .map(p => `${p.pick.player.firstName} ${p.pick.player.lastName} -> ${p.projectionStatus.status}`)
                    .join(', ');
                logger.debug('SyncPicks - Market status changed for some picks, updating them in the betslip', players);
                updateBetslipProjections(picksWithChangedMarketStatus);
            }

            const picksToRemove = invalidPicksByEvent.flatMap(it => it.invalidPicks).concat(picksWithMissingEvents);
            const toRemove = picksToRemove.map(it => {
                return { eventId: it.pick.eventId, player: it.pick.player };
            });

            const onModalDismiss = infoModalDismissBehaviour === 'goBack' ? () => navigation.goBack() : undefined;

            if (expectError && picksToRemove.length === 0 && picksWithChangedProjections.length === 0) {
                //If we were expecting an error (this sync was made as a result of a 402 submit entry code)
                //but we did not find any picks with issues, show the generic entry submit error
                showInfoSheet({
                    title: t('error:betslip_pickem.failed_submit_entry'),
                    description: getSubmitEntryErrorMsg(),
                    buttonLabel: t('common:try_again'),
                    containerStyle: { paddingBottom: 0 },
                    icon: (
                        <Box paddingTop={'s16'}>
                            <ErrorIcon />
                        </Box>
                    ),
                    onDismiss: onModalDismiss,
                });
            } else if (acceptAllProjectionsChanges && picksWithChangedProjections.length > 0) {
                if (promptIfOddsChanged) {
                    logger.info(
                        LOG_TAG,
                        'Projections changed during submit entry. Show the pre-submit odds changed modal.'
                    );
                    showInfoLineupUpdateModal({ picksWithChangedProjections, onDismiss: onModalDismiss });
                    return;
                } else {
                    logger.info(
                        LOG_TAG,
                        'Some projections changed.User auto-accepts projection changes. Update them locally.'
                    );
                    runStoreUpdate(() => {
                        updateBetslipProjections(picksWithChangedProjections);
                    });
                }
                //if there are also removed picks, show the modal
                if (toRemove.length > 0) {
                    showLineupUpdateModal({
                        picksWithMissingEvents,
                        invalidPicksByEvent,
                        playersRemoved: toRemove,
                        onDismiss: onModalDismiss,
                    });
                }
            } else {
                // user does not accept projection changes automatically
                // or there are projection changes in this sync and/or removed players
                // show the lineup update modal

                showLineupUpdateModal({
                    invalidPicksByEvent,
                    picksWithMissingEvents,
                    picksWithChangedProjections,
                    playersRemoved: toRemove,
                    onDismiss: onModalDismiss,
                });
            }
        },
        [
            gqlClient,
            acceptAllProjectionsChanges,
            setIsSyncingPicks,
            updateStorePlayersProjections,
            navigation,
            t,
            showInfoLineupUpdateModal,
            updateBetslipProjections,
            showLineupUpdateModal,
            showInfoSheet,
        ]
    );

    const validateEntryAmount = useCallback(
        (
            amount: number,
            selectedCurrency: Currency,
            gameSpecificValidationData: BetValidationData | undefined
        ): EntryAmountValidationData => {
            // TODO: extract and add tests
            const errors = gameSpecificValidationData?.errors ?? [];
            const maxEntryAmount = gameSpecificValidationData?.maxAmount ?? 0;

            const minEntryAmount = gameSpecificValidationData?.minAmount ?? 1;
            const userWagerLimits = userLimits ?? [];

            let error: string | undefined,
                validEntry = true;
            const limits = [];
            const walletMax = selectedCurrency === Currency.Fre ? betrBucks : realMoneyTotal;
            const balanceType = selectedCurrency === Currency.Fre ? 'Betr Bucks' : 'cash';

            limits.push({ limit: walletMax, msg: t('above_playable_balance', { balanceType, limit: walletMax }) });
            // BE always returns the errors in the exact order in which we should display them
            limits.push({
                limit: maxEntryAmount,
                msg: errors?.[0]?.message,
                minLimit: minEntryAmount,
            });

            userWagerLimits.forEach(limit => {
                const remainingAmount = Math.max(0, limit.amount - limit.current_state);
                limits.push({
                    limit: remainingAmount,
                    msg: getWagerLimitErrorMessage(amount.toString(), userWagerLimits),
                });
            });

            const sortedLimits = limits.sort((a, b) => a.limit - b.limit);
            const breachedLimit = sortedLimits.find(it => amount > it.limit || (it.minLimit && amount < it.minLimit));

            if (breachedLimit) {
                validEntry = false;
                error = breachedLimit.msg;
            }

            // exclude from the calculation of maxAllowed the walletMax limit
            const limitsWithoutWalletMax = sortedLimits.filter(it => it.limit !== walletMax);
            // get the first limit (smallest) and if that doesn't exist, use the maxEntryAmount
            const maxAllowed =
                limitsWithoutWalletMax.length > 0
                    ? Math.min(...limitsWithoutWalletMax.map(it => it.limit))
                    : maxEntryAmount;
            return { validEntry, errorMessage: error, maxAllowedEntryAmount: maxAllowed };
        },
        [betrBucks, realMoneyTotal, t, userLimits]
    );

    const processValidateEntryResponse = useCallback(
        (userEntry: number, userEntryCurrency: Currency, validationData: BetValidationData[]) => {
            const limitsValidation: Record<GameMode, EntryAmountValidationData> = {
                DYNAMIC: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
                PERFECT: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
                MYSTERY: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
                RACE_TO_PLACE: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
            };

            // parse the validation data and get the limits for each game mode
            Object.keys(limitsValidation).forEach(key => {
                const gameSpecificRulesId = allEntryRules.find(it => it.gameMode === key)?.id;
                const gameSpecificValidationData = validationData.find(it => it.entryRulesId === gameSpecificRulesId);
                limitsValidation[key as GameMode] = validateEntryAmount(
                    userEntry,
                    userEntryCurrency,
                    gameSpecificValidationData
                );
            });

            const defaultValidationResult = mapValidationDataToGameModes(
                validationData,
                allEntryRules,
                limitsValidation
            ).DEFAULT.betValidation;

            if (!defaultValidationResult) {
                throw Error(t('error:betslip_pickem.perfect_play_validation_not_found'));
            }

            // set the validation data (rules we get from BE and limits that we handle on FE) in the store
            runStoreUpdate(() => {
                setValidationData(validationData, allEntryRules, limitsValidation);
            });

            return defaultValidationResult;
        },
        [allEntryRules, setValidationData, t, validateEntryAmount]
    );

    const validatePicks = useCallback(
        async (
            picks: BetslipPick[],
            userEntry: number,
            userEntryCurrency: Currency,
            latestPick: BetslipPick | undefined,
            removeFunction: (picks: { eventId: string; player: PlayerWithTeam }[]) => void,
            executionMode: 'cancelPrevious' | 'ignoreIfRunning' = 'cancelPrevious'
        ) => {
            const selectedPromo = useBetslipStore.getState().selectedPromo;

            if (picksInDisabledLeagues(picks)) {
                syncPicks({ expectError: false });
            }

            if (executionMode === 'cancelPrevious') {
                validatingPicksPromise?.cancel();
            } else if (executionMode === 'ignoreIfRunning' && validatingPicksPromise?.running) {
                logger.debug(LOG_TAG, 'Validate picks already runing. Do not fire a new one.');
                return;
            }

            const rulesIds = allEntryRules.map(it => it.id).filter(Boolean);
            const execute = async () => {
                lastPickToValidate = latestPick ?? lastPickToValidate;
                const pickToValidate = lastPickToValidate;

                const appliedPromotionIds = [selectedPromo?.promotion.id]
                    .filter(Boolean)
                    .filter(id => id !== 'mystery');

                validatingPicksPromise = CancelablePromise.from(
                    gqlClient
                        .query<ValidateEntryWithRulesQuery, ValidateEntryWithRulesQueryVariables>(
                            ValidateEntryWithRulesDocument,
                            {
                                rulesIds,
                                entry: {
                                    amount: userEntry,
                                    currency: userEntryCurrency,
                                    picks: mapPicksToApiData(picks),
                                    appliedPromotionIds: appliedPromotionIds,
                                },
                            },
                            { requestPolicy: 'network-only' }
                        )
                        .toPromise()
                );

                const validateResponse = await validatingPicksPromise;

                if (validateResponse.data) {
                    const defaultValidationResult = processValidateEntryResponse(
                        userEntry,
                        userEntryCurrency,
                        validateResponse.data.validateEntryOnEntryRules
                    );

                    logger.info(LOG_TAG, 'Picks validated:', defaultValidationResult.status, defaultValidationResult);

                    //See ./processors/validation-processing.md for more details about this implementation
                    const promotionsProcessor = new PromotionsProcessor({
                        modalContext: { showPromoRemovedModal },
                        latestPick: pickToValidate,
                        removeFunction,
                        reValidatePicks: () => {
                            validatePicks(picks, userEntry, userEntryCurrency, pickToValidate, removeFunction);
                        },
                    });

                    const restrictionsProcessor = new PicksRestrictionsProcessor({
                        alertsContext,
                        latestPick: pickToValidate,
                        removeFunction,
                        restrictedComboAlertContext,
                    });

                    const edgeComboProcessor = new PicksEdgeComboProcessor({
                        edgeComboDialogContext,
                        removeFunction,
                        userSettings,
                    });

                    const invalidPicksProcessor = new InvalidPicksProcessor({
                        syncPicks,
                    });

                    const exceedsEdgeComboProcessor = new PicksExceedsEdgeComboProcessor({
                        alertsContext,
                        latestPick: pickToValidate,
                        removeFunction,
                        openBetslip: () => {
                            navigateToPickslip(false, true);
                            dismissAll();
                        },
                    });

                    const errorChain = ChainProcessors.of(
                        invalidPicksProcessor,
                        restrictionsProcessor,
                        exceedsEdgeComboProcessor,
                        edgeComboProcessor
                    );
                    const successChain = ChainProcessors.of(edgeComboProcessor);

                    //First check promotions, then proceed to check based on validation result status
                    promotionsProcessor.setNextProcessor(
                        new PicksValidateResultProcessor({})
                            //on success just check edge combos
                            .withSuccessProcessor(successChain)
                            //on error proceed to the regular chain of processors [restrictions->]
                            .withErrorProcessor(errorChain)
                    );
                    promotionsProcessor.process(validateResponse.data);
                } else {
                    setValidating(false);
                    logger.warn(LOG_TAG, 'No response for validate betslip. Is network down!?');
                }
            };
            try {
                setValidating(true);
                await execute();
            } catch (e) {
                logger.warn(LOG_TAG, 'Error validating picks', e);
                setValidating(false);
            } finally {
                //finally - last pick was validated, so we can clear it
                lastPickToValidate = undefined;
                dismissValidationSheet();
            }
        },
        [
            picksInDisabledLeagues,
            allEntryRules,
            syncPicks,
            gqlClient,
            processValidateEntryResponse,
            showPromoRemovedModal,
            alertsContext,
            restrictedComboAlertContext,
            edgeComboDialogContext,
            userSettings,
            navigateToPickslip,
            dismissAll,
            setValidating,
            dismissValidationSheet,
        ]
    );

    const removeSelection = useCallback(
        (picksToRemove: { eventId: string; player: PlayerWithTeam }[], validate: boolean = true) => {
            const validating = useBetslipStore.getState().validating;
            const entryAmount = useBetslipStore.getState().entryAmount ?? 0;
            const currency = useBetslipStore.getState().currency;
            if (validating) {
                showBetslipValidatingDialog();
                return;
            }
            logger.debug(LOG_TAG, 'Remove player selection...');
            const newPicks = removePicks(picksToRemove);
            triggerHapticFeedback();
            if (validate) {
                validatePicks(newPicks, entryAmount, currency, undefined, removeSelection);
            }
        },
        [removePicks, showBetslipValidatingDialog, validatePicks]
    );

    const addSelection = useCallback(
        async (pick: BetslipPick) => {
            const validating = useBetslipStore.getState().validating;
            const entryAmount = useBetslipStore.getState().entryAmount ?? 0;
            const currency = useBetslipStore.getState().currency;
            if (validating) {
                showBetslipValidatingDialog();
                return;
            }

            logger.debug(LOG_TAG, 'Add player selection', pick.projection.name, pick.projection.type, pick.outcome);
            triggerHapticFeedback();
            const newPicks = selectPick(pick);
            validatePicks(newPicks, entryAmount, currency, pick, removeSelection);
        },
        [removeSelection, selectPick, showBetslipValidatingDialog, validatePicks]
    );

    return {
        addSelection,
        removeSelection,
        syncPicks,
        validatePicks,
        validateEntryAmount,
    };
};
